import React from 'react';
import { Page, Text, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';

const getYear = () => {
    return new Date().getFullYear();
}

const getPastYear = () => {
    return new Date().getFullYear() - 1;
}

const getCurrentDate = () => {
    const date = new Date();
  
    // Tableau des mois en français
    const months = [
      "janvier", "février", "mars", "avril", "mai", "juin",
      "juillet", "août", "septembre", "octobre", "novembre", "décembre"
    ];
  
    // Format : jour mois année (ex : 19 mars 2024)
    const day = date.getDate(); // Le jour du mois
    const month = months[date.getMonth()]; // Le mois en français
    const year = date.getFullYear(); // L'année
  
    return `${day} ${month} ${year}`;
};

Font.register({
    family: 'Roboto',
    fonts: [
      { src: 'https://fonts.gstatic.com/s/roboto/v29/KFOmCnqEu92Fr1Mu4mxP.ttf', fontWeight: 400 }, // Regular
      { src: 'https://fonts.gstatic.com/s/roboto/v29/KFOkCnqEu92Fr1Mu51xIIzI.ttf', fontWeight: 700 }, // Bold
      { src: 'https://fonts.gstatic.com/s/roboto/v29/KFOjCnqEu92Fr1Mu51TjASc.ttf', fontStyle: 'italic', fontWeight: 400 }, // Italic
    ],
});

const getGender = (gender) => {
    if (gender === 'M.') {
        return 'Monsieur'
    } else {
        return 'Madame'
    }
}

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        alignItems: "center",
        width: "100%",
        padding: 10
    },
    title: {
        fontSize: 13,
        fontWeight: 20,
        width: "100%",
        textAlign: "center"
    },
    body: {
        fontFamily: "Helvetica-Oblique",
        fontSize: 14,
        fontWeight: "light",
        color: "grey"
    },
    bodyBold: {
        fontFamily: "Helvetica-BoldOblique",
        fontSize: 14,
    },
    footer: {
        fontFamily: "Helvetica",
        fontSize: 8
    }
});

export default function TaxationDocument(props) {

    const documentTitle = "Attestation fiscale " + props.client.gender + " " + props.client.name + " " + getYear();

    const pastYear = getPastYear();

    const currentYear = getYear();

    const genderMark = getGender(props.client.gender);

    const currentDate = getCurrentDate();

    const totalNumber = parseFloat(props.total);

    const tiersNumber = parseFloat(props.tiers);

    const endTotal = totalNumber + tiersNumber;

    const formatted = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(totalNumber).replace(/,/g, ' ');

    const formattedTiers = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(tiersNumber).replace(/,/g, ' ');

    const formattedEndTotal = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(endTotal).replace(/,/g, ' ');

    return (
        <Document title={documentTitle}>
            <Page size="A4" style={styles.page} orientation='portrait'>
                <view style={{flexDirection: "row", width: "80%", justifyContent: "space-between", alignItems: "flex-end", marginTop: "20px"}}>
                    <Image src="/logo.png" style={{height: 120}}/>
                    <view style={{textAlign: "center", display: "flex", width: "30%"}}>
                        <Text style={styles.title}>{props.client.gender} {props.client.firstname} {props.client.name}</Text>
                        <Text style={styles.title}>{props.client.adresse}</Text>
                        <Text style={styles.title}>{props.client.code} {props.client.ville}</Text>
                    </view>
                </view>
                <view style={{textAlign: "center", display: "flex", width: "80%", justifyContent: "space-between", flexDirection: "row", marginTop: "30px"}}>
                    <Text style={{fontWeight: "heavy", fontSize: 12}}>Réf:  {props.client.nbr}</Text>
                    <Text style={{fontWeight: "heavy", fontSize: 12}}>{currentDate}</Text>
                </view>
                <view style={{textAlign: "center", display: "flex", width: "80%", justifyContent: "center", flexDirection: "row", marginTop: "30px"}}>
                    <Text style={{fontWeight: "black", fontSize: 20, textDecoration: "underline"}}>ATTESTATION FISCALE {pastYear}</Text>
                </view>
                <view style={{textAlign: "left", display: "flex", width: "80%", justifyContent: "start", flexDirection: "row", marginTop: "30px"}}>
                    <Text style={styles.body}>
                        {genderMark},
                        {"\n"}{"\n"}
                        Notre société étant agréée pour les services à la personne, nos prestations ouvrent
                        droit à réduction fiscale. Cette réduction porte sur la partie services à domicile de la
                        prestation et correspond à 50 % du montant total pour l'année.
                        {"\n"}{"\n"}
                        Pour vous permettre de renseigner votre déclaration, nous avons calculé pour vous le
                        total des sommes que vous avez réglées au cours de l'année {pastYear} au titre du service à
                        domicile. Ce total correspond à la somme des lignes Dont services des factures qui vous
                        ont été encaissées pour cette même année.
                        {"\n"}{"\n"}
                        Pour bénéficier de la réduction, vous devrez donc reporter sur votre déclaration, dans
                        la rubrique <Text style={styles.bodyBold}>« 7 - Réductions et crédits d’impôt – dans la case « Services à la personne,
                        emploi à domicile - Dépenses d’emploi à domicile – 7DB »</Text>" la somme indiquée dans
                        la case Total Assiette Déduction Fiscale ci-après.
                        {"\n"}{"\n"}
                        Vous devrez compléter l’imprimé <Text style={styles.bodyBold}>{currentYear} RICI</Text> – Réductions d’impôt/Crédits d’impôt en
                        détaillant si nécessaire les différentes prestations :
                        {"\n"}
                        - Entretien de la maison et travaux ménagers – <Text style={styles.bodyBold}>BDF</Text>
                        {"\n"}
                        - Petits travaux de jardinage - <Text style={styles.bodyBold}>BDG</Text>
                        {"\n"}
                        - Petits travaux de bricolage - <Text style={styles.bodyBold}>BDH</Text>
                        {"\n"}
                        - Livraison de repas à domicile - <Text style={styles.bodyBold}>BDN</Text>
                        {"\n"}{"\n"}
                        Nous restons bien sûr à votre disposition pour tout renseignement complémentaire.
                    </Text>
                </view>
                <view style={{textAlign: "end", display: "flex", width: "90%", justifyContent: "flex-end", flexDirection: "row", marginTop: "30px"}}>
                    <Text style={styles.body}>
                        Votre agence Les Lutins Gourmets
                    </Text>
                </view>
                <view style={{textAlign: "center", display: "flex", width: "90%", justifyContent: "center", flexDirection: "column", position: "absolute", bottom: "10px"}}>
                    <Text style={styles.footer}>
                        <Text style={{color: "green"}}>Les Lutins Gourmets</Text> – 251, route des Bauches – 74330 Sillingy
                    </Text>
                    <Text style={styles.footer}>
                        Tél : 04.50.77.16.60 - Email : gestion@lutins-gourmets.com
                    </Text>
                    <Text style={styles.footer}>
                        SAS au capital de 12 500€ - 900 848 383 RCS ANNECY
                    </Text>
                </view>
            </Page>
            <Page size="A4" style={styles.page} orientation='portrait'>
                <view style={{flexDirection: "row", width: "80%", justifyContent: "space-between", alignItems: "flex-end", marginTop: "20px"}}>
                    <Image src="/logo.png" style={{height: 120}}/>
                    <view style={{textAlign: "left", display: "flex", width: "60%"}}>
                        <Text style={{fontWeight: "black", fontSize: 20, textDecoration: "underline"}}>ATTESTATION FISCALE</Text>
                    </view>
                    <Image src="/sap-transparent.png" style={{height: 30}}/>
                </view>
                <view style={{textAlign: "center", display: "flex", width: "80%", justifyContent: "space-between", flexDirection: "row", marginTop: "30px"}}>
                    <Text style={{fontWeight: "heavy", fontSize: 12}}>Réf:  {props.client.nbr}</Text>
                    <Text style={{fontWeight: "heavy", fontSize: 12}}>{currentDate}</Text>
                </view>
                <view style={{display: "flex", flexDirection: "column", justifyContent: "start", textAlign: "left", marginTop: "20px", width: "80%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left"}}>{props.client.gender} {props.client.firstname} {props.client.name}</Text>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left"}}>{props.client.adresse} - {props.client.code} {props.client.ville}</Text>
                </view>
                <view style={{display: "flex", flexDirection: "column", justifyContent: "start", textAlign: "left", marginTop: "20px", width: "80%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", textDecoration: "underline"}}>Services encaissés :</Text>
                </view>
                <view style={{display: "flex", flexDirection: "column", justifyContent: "start", textAlign: "left", marginTop: "20px", width: "80%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>Période {props.periodeStart} à {props.periodeEnd}</Text>
                </view>
                <view style={{display: "flex", flexDirection: "row", justifyContent: "space-between", textAlign: "center", marginTop: "20px", width: "70%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>Total Service encaissé</Text>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>{formattedEndTotal} €</Text>
                </view>
                <view style={{display: "flex", flexDirection: "row", justifyContent: "space-between", textAlign: "center", marginTop: "5px", width: "70%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>Dont livraison de repas à domicile</Text>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>{formatted} €</Text>
                </view>
                <view style={{display: "flex", flexDirection: "row", justifyContent: "space-between", textAlign: "center", marginTop: "5px", width: "70%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>Dont autres prestations :</Text>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left", color: "grey"}}>{formattedTiers} €</Text>
                </view>
                <view style={{display: 'flex', borderTop: "1px solid black", width: "70%", marginTop: "3px"}}/>
                <view style={{display: "flex", flexDirection: "row", justifyContent: "space-between", textAlign: "center", marginTop: "15px", width: "70%"}}>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left"}}>Total Assiette déduction fiscale :</Text>
                    <Text style={{fontFamily: "Helvetica", fontSize: 14, textAlign: "left"}}>{formattedEndTotal} €</Text>
                </view>
                <view style={{textAlign: "center", display: "flex", width: "90%", justifyContent: "center", flexDirection: "column", position: "absolute", bottom: "10px"}}>
                    <Text style={styles.footer}>
                        <Text style={{color: "green"}}>Les Lutins Gourmets</Text> – 251, route des Bauches – 74330 Sillingy
                    </Text>
                    <Text style={styles.footer}>
                        Tél : 04.50.77.16.60 - Email : gestion@lutins-gourmets.com
                    </Text>
                    <Text style={styles.footer}>
                        SAS au capital de 12 500€ - 900 848 383 RCS ANNECY
                    </Text>
                </view>
            </Page>
        </Document>
    )
}